// Manage.tsx — экран «Управление» (Ф8, owner/director). Респонденты: смена роли,
// создание + инвайт-ссылка, перевыпуск инвайта, деактивация. Веб-аналог
// /add-respondent / /respondents. Расписание назначается на экране «Расписание».

import { useState } from 'react';
import { api, ApiError } from '../api/client';
import type { InviteResponse, RespondentRoleContract } from '../api/contract';
import { StateCard } from '../components/StateBlock';
import { useApi } from '../lib/useApi';

const ROLE_LABEL: Record<string, string> = {
  director: 'директор',
  manager: 'менеджер',
  admin: 'админ',
};
const ROLES: RespondentRoleContract[] = ['manager', 'director', 'admin'];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export default function Manage() {
  const [reloadKey, setReloadKey] = useState(0);
  const state = useApi(() => api.respondents(), [reloadKey]);
  const reload = () => setReloadKey((k) => k + 1);
  const [invite, setInvite] = useState<InviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось');
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) return <StateCard kind="loading" message="Загрузка сотрудников…" />;
  if (state.error || !state.data) {
    return <StateCard kind="error" message={state.error ?? 'Не удалось загрузить'} />;
  }

  const respondents = state.data.respondents;

  return (
    <section className="page">
      <div className="ph">
        <h1>Управление</h1>
      </div>
      <p className="sub">
        Респонденты, привязка к Telegram, согласие ФЗ-152, инвайты (веб-аналог{' '}
        <span className="toks">/add-respondent</span>, <span className="toks">/respondents</span>).
      </p>

      {invite && <InviteBanner invite={invite} onClose={() => setInvite(null)} />}
      {error && <p style={{ color: 'var(--red)', fontSize: 13 }}>{error}</p>}

      <div className="card">
        <table className="mgmt">
          <thead>
            <tr>
              <th>Сотрудник</th>
              <th>Роль</th>
              <th>Telegram</th>
              <th>Согласие</th>
              <th>Активен</th>
              <th>Инвайт</th>
            </tr>
          </thead>
          <tbody>
            {respondents.map((r) => (
              <tr key={r.id} style={{ opacity: r.isActive ? 1 : 0.55 }}>
                <td>
                  <b>{r.name}</b>
                </td>
                <td>
                  <select
                    value={r.role}
                    disabled={busy}
                    onChange={(e) =>
                      void run(() =>
                        api.changeRole(r.id, { role: e.target.value as RespondentRoleContract }),
                      )
                    }
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABEL[role] ?? role}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  {r.telegramLinked ? (
                    <span className="chip ok">привязан</span>
                  ) : (
                    <span className="chip warn">ждёт</span>
                  )}
                </td>
                <td>
                  {r.consentAt ? (
                    <span className="chip ok">✓ {fmtDate(r.consentAt)}</span>
                  ) : (
                    <span className="chip off">не дано</span>
                  )}
                </td>
                <td>
                  <button
                    className="btn ghost"
                    style={{ padding: '4px 10px' }}
                    disabled={busy}
                    onClick={() => void run(() => api.updateRespondent(r.id, { isActive: !r.isActive }))}
                  >
                    {r.isActive ? 'Деактивировать' : 'Активировать'}
                  </button>
                </td>
                <td>
                  {r.telegramLinked ? (
                    <span className="toks">—</span>
                  ) : (
                    <button
                      className="btn ghost"
                      style={{ padding: '4px 10px' }}
                      disabled={busy}
                      onClick={() =>
                        void run(async () => setInvite(await api.reissueInvite(r.id)))
                      }
                    >
                      Ссылка
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateRespondent busy={busy} onCreated={(inv) => { setInvite(inv); reload(); }} setError={setError} />
    </section>
  );
}

function InviteBanner({ invite, onClose }: { invite: InviteResponse; onClose: () => void }) {
  const text = invite.inviteUrl ?? invite.inviteToken;
  return (
    <div className="card" style={{ borderColor: 'var(--green)', marginBottom: 14 }}>
      <h3>🔗 Одноразовая инвайт-ссылка</h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>
        Передай в личку сотруднику — он привяжет Telegram к боту. Показывается один раз.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code className="toks" style={{ flex: 1, wordBreak: 'break-all', padding: '8px 10px' }}>
          {text}
        </code>
        <button className="btn" onClick={() => void navigator.clipboard?.writeText(text)}>
          Скопировать
        </button>
        <button className="btn ghost" onClick={onClose}>
          Скрыть
        </button>
      </div>
      {!invite.inviteUrl && (
        <p style={{ color: 'var(--yellow)', fontSize: 12, margin: '8px 0 0' }}>
          Username бота не определён на сервере — показан сырой токен. Ссылка:{' '}
          <span className="toks">https://t.me/&lt;бот&gt;?start={invite.inviteToken}</span>
        </p>
      )}
    </div>
  );
}

function CreateRespondent({
  busy,
  onCreated,
  setError,
}: {
  busy: boolean;
  onCreated: (inv: InviteResponse) => void;
  setError: (s: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<'director' | 'manager'>('manager');
  const [saving, setSaving] = useState(false);

  async function create() {
    setSaving(true);
    setError(null);
    try {
      const inv = await api.createRespondent({ name: name.trim(), role });
      onCreated(inv);
      setOpen(false);
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <div className="addrow" style={{ marginTop: 16 }}>
        <button onClick={() => setOpen(true)}>+ Добавить респондента</button>
      </div>
    );
  }
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3>Новый респондент</h3>
      <p style={{ color: 'var(--muted)', fontSize: 12, margin: '0 0 10px' }}>
        Создаёт человека и одноразовую инвайт-ссылку. Расписание анкет назначьте на экране
        «Расписание».
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Имя</label>
          <input className="tinput" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>Роль</label>
          <select
            className="tselect"
            value={role}
            onChange={(e) => setRole(e.target.value as 'director' | 'manager')}
          >
            <option value="manager">менеджер</option>
            <option value="director">директор</option>
          </select>
        </div>
        <button className="btn" disabled={saving || busy || name.trim() === ''} onClick={create}>
          {saving ? 'Создаю…' : 'Создать + ссылка'}
        </button>
        <button className="btn ghost" onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </div>
  );
}
